import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    // Set video sources
    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) {
        source.setAttribute('src', videoSource);
      }
    }

    // iPhone/iOS autoplay requirements
    videoElement.muted = true;
    videoElement.defaultMuted = true;
    videoElement.setAttribute('muted', '');
    videoElement.setAttribute('playsinline', '');
    videoElement.setAttribute('webkit-playsinline', '');
    videoElement.setAttribute('autoplay', '');
    videoElement.setAttribute('loop', '');

    // Load the dynamically assigned source
    videoElement.load();

    // Explicitly attempt autoplay after loading
    const playVideo = () => {
      videoElement.muted = true;

      const promise = videoElement.play();

      if (promise !== undefined) {
        promise.catch(() => {
          // iOS may reject autoplay in some situations.
          // The video remains muted/inline and can be retried.
        });
      }
    };

    // Try after metadata/source is ready
    videoElement.addEventListener('loadedmetadata', playVideo, {
      once: true,
    });

    // Additional attempt after the browser finishes the current task
    setTimeout(playVideo, 100);
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}